import type { Response } from 'lib-shared-legacy-types';
import { v4 as uuidv4 } from 'uuid';

import { ajaxSend } from 'api/api';
import type { AnnouncementT } from 'components/powerCallFlow/components/types';
import type { AudioT } from 'components/shared/audio/api/types';

import { Adapter, Item } from './index';

export interface AnnouncementItem {
  url: string;
  label: string;
  author: string;
}

interface PrivateItem extends Item {
  author: string;
}

type GetItemsResponse = Response<{
  uploads: AnnouncementT[];
}>;

type PresignResponse = Response<{
  download_url: string;
  upload_url: string;
}>;

export default class AnnouncementAdapter implements Adapter {
  private _items: PrivateItem[] = [];

  getItemForCallback(id: string): AnnouncementItem | undefined {
    const items = this._items;
    const item = items.find((item) => item.id === id);

    if (!item) {
      return undefined;
    }

    const { id: _id, name, ...rest } = item;

    return {
      ...rest,
      label: name || '',
    };
  }

  getItemFromURL(url: string): Item | undefined {
    const items = this._items;

    return items.find((item) => item.url === url);
  }

  getFormattedItem(announcement: AudioT | AnnouncementItem): Item | undefined {
    const { url } = announcement as AnnouncementItem;
    const items = this._items;

    return items.find((item) => item.url === url);
  }

  async getItems(): Promise<Item[]> {
    const {
      data: { uploads },
    } = await ajaxSend<GetItemsResponse>({
      path: '/v1/uploads/announcement',
    });

    const items = uploads.map<Item>(({ pretty_name: name, url }) => ({
      id: uuidv4(),
      name,
      url,
    }));

    this._items = items.map((item, index) => ({
      ...item,
      author: uploads[index].created_by?.name || '',
    }));

    return items;
  }

  async upload(file: File): Promise<string> {
    const {
      data: { download_url, upload_url },
    } = await ajaxSend<PresignResponse>({
      path: '/v1/uploads/presigned_url/announcement',
      method: 'POST',
      data: {
        pretty_name: file.name,
        content_type: file.type,
      },
    });

    await fetch(upload_url, {
      method: 'PUT',
      headers: {
        contentType: file.type,
      },
      body: file,
    });

    return download_url;
  }
}
